import { useState, useEffect, useCallback } from "react";
import {
  Server,
  Loader2,
  AlertTriangle,
  Users,
  Shield,
  Save,
  Activity,
  Eye,
  EyeOff,
} from "lucide-react";
import { useToastStore } from "../../stores/toastStore";
import { getAPI, postAPI } from "../../api/frameworksApi";
import { API_URL } from "../../constants";
import { validateLDAPURL } from "../../utils/validators";

/* ── Types ─────────────────────────────────────────────── */

type IdentitySource = "LDAP" | "OIDC" | "NONE";

interface IdentityConfig {
  id?: string;
  tenantId?: string;
  identitySource: IdentitySource;
  ldapUrl: string;
  ldapBaseDn: string;
  ldapBindDn: string;
  ldapBindPassword?: string;
  ldapPasswordSet?: boolean;
  ldapUserSearchBase: string;
  ldapUserSearchFilter: string;
  ldapUserEmailAttr: string;
  ldapUserDisplayNameAttr: string;
  ldapGroupSearchBase: string;
  ldapGroupNameAttr: string;
  ldapGroupMemberAttribute: string;
  oidcIssuerUri?: string;
  oidcClientId?: string;
  oidcClientSecretSet?: boolean;
  active?: boolean;
  configured?: boolean;
  lastTestAt?: string;
  lastTestResult?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface TestResult {
  success: boolean;
  message: string;
  details?: Record<string, unknown>;
}

const DEFAULTS: IdentityConfig = {
  identitySource: "LDAP",
  ldapUrl: "",
  ldapBaseDn: "",
  ldapBindDn: "",
  ldapBindPassword: "",
  ldapUserSearchBase: "",
  ldapUserSearchFilter: "",
  ldapUserEmailAttr: "",
  ldapUserDisplayNameAttr: "",
  ldapGroupSearchBase: "",
  ldapGroupNameAttr: "",
  ldapGroupMemberAttribute: "",
};

const ATTR_FIELDS: (keyof IdentityConfig)[] = [
  "ldapUserSearchFilter",
  "ldapUserEmailAttr",
  "ldapUserDisplayNameAttr",
  "ldapGroupNameAttr",
  "ldapGroupMemberAttribute",
];

const ATTR_LABELS: Record<string, string> = {
  ldapUserSearchFilter: "Username attribute",
  ldapUserEmailAttr: "Email attribute",
  ldapUserDisplayNameAttr: "Display name attribute",
  ldapGroupNameAttr: "Group name attribute",
  ldapGroupMemberAttribute: "Group member attribute",
};

const ATTR_NAME_REGEX = /^[A-Za-z][A-Za-z0-9-]*$/;

/* ── Validation ────────────────────────────────────────── */

interface ValidationErrors {
  [key: string]: string;
}

function validate(config: IdentityConfig): ValidationErrors {
  const errors: ValidationErrors = {};

  const ldapUrlValidation = validateLDAPURL(config.ldapUrl);
  if (!ldapUrlValidation.valid) {
    errors.ldapUrl = ldapUrlValidation.error || "Server URL is required";
  }

  if (!config.ldapBaseDn.trim()) errors.ldapBaseDn = "Base DN is required";
  if (!config.ldapBindDn.trim()) errors.ldapBindDn = "Bind DN is required";
  if (!config.ldapPasswordSet && !config.ldapBindPassword?.trim())
    errors.ldapBindPassword = "Bind password is required";

  ATTR_FIELDS.forEach((key) => {
    const raw = (config[key] as string | undefined) ?? "";
    const value = raw.trim();
    if (!value) return;
    if (!ATTR_NAME_REGEX.test(value)) {
      errors[key] =
        "Enter a single LDAP attribute name (letters, digits, hyphen). No parentheses, equals signs, or spaces.";
    }
  });
  return errors;
}

/* ── Component ─────────────────────────────────────────── */

export function DirectoryConfiguration() {
  const [config, setConfig] = useState<IdentityConfig>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [showPassword, setShowPassword] = useState(false);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAPI(API_URL.IDENTITY_CONFIG);
      if (res?.data) {
        setConfig({ ...DEFAULTS, ...res.data });
      }
    } catch (err: any) {
      if (err?.response?.status !== 404)
        useToastStore
          .getState()
          .showError("Error", "Failed to load configuration");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const updateField = <K extends keyof IdentityConfig>(
    key: K,
    value: IdentityConfig[K],
  ) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const buildPayload = () => {
    const payload: Record<string, unknown> = {
      identitySource: "LDAP" as IdentitySource,
      ldapUrl: config.ldapUrl,
      ldapBaseDn: config.ldapBaseDn,
      ldapBindDn: config.ldapBindDn,
      ldapUserSearchBase: config.ldapUserSearchBase,
      ldapUserSearchFilter: config.ldapUserSearchFilter,
      ldapUserEmailAttr: config.ldapUserEmailAttr,
      ldapUserDisplayNameAttr: config.ldapUserDisplayNameAttr,
      ldapGroupSearchBase: config.ldapGroupSearchBase,
      ldapGroupNameAttr: config.ldapGroupNameAttr,
      ldapGroupMemberAttribute: config.ldapGroupMemberAttribute,
    };
    if (config.ldapBindPassword?.trim())
      payload.ldapBindPassword = config.ldapBindPassword;
    return payload;
  };

  const handleTest = async () => {
    const v = validate(config);
    if (Object.keys(v).length) {
      setErrors(v);
      return;
    }
    setTesting(true);
    try {
      const res = await postAPI(API_URL.IDENTITY_CONFIG_TEST, buildPayload());
      const result: TestResult = res?.data;
      if (result?.success) {
        useToastStore
          .getState()
          .showSuccess(
            "Success",
            result.message || "Connection test successful",
          );
      } else {
        useToastStore
          .getState()
          .showError("Error", result?.message || "Connection test failed");
      }
    } catch (err: any) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err?.response?.data?.message ||
            err.message ||
            "Connection test failed",
        );
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    const v = validate(config);
    if (Object.keys(v).length) {
      setErrors(v);
      return;
    }
    setSaving(true);
    try {
      const res = await postAPI(API_URL.IDENTITY_CONFIG, buildPayload());
      if (res?.data) {
        setConfig({ ...DEFAULTS, ...res.data });
      }
      useToastStore
        .getState()
        .showSuccess("Success", "Configuration saved successfully");
    } catch (err: any) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err?.response?.data?.message ||
            err.message ||
            "Failed to save configuration",
        );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1>Directory Configuration</h1>
          <p className="text-sm d mt-0.5">
            Configure LDAP / Active Directory for user authentication and
            directory sync
          </p>
        </div>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="size-5 animate-spin d" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Page Header ── */}
      <div>
        <h1>Directory Configuration</h1>
        <p className="text-sm d mt-0.5">
          Configure LDAP / Active Directory for user authentication and
          directory sync
        </p>
      </div>

      {/* ── Server Connection ── */}
      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border bg-[#f8fafc] dark:bg-white/[0.02]">
          <div className="flex items-center gap-2.5">
            <div className="size-8 rounded-lg bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center">
              <Server className="size-4 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold">Server Connection</h3>
              <p className="text-[11px] d">
                LDAP server endpoint and bind credentials
              </p>
            </div>
          </div>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">
            <Field
              label="Server URL"
              value={config.ldapUrl}
              onChange={(v) => updateField("ldapUrl", v)}
              placeholder="e.g. ldap://ldap.example.com:389"
              error={errors.ldapUrl}
              required
            />
            <Field
              label="Base DN"
              value={config.ldapBaseDn}
              onChange={(v) => updateField("ldapBaseDn", v)}
              placeholder="e.g. dc=example,dc=com"
              error={errors.ldapBaseDn}
              required
            />
            <Field
              label="Bind DN"
              value={config.ldapBindDn}
              onChange={(v) => updateField("ldapBindDn", v)}
              placeholder="e.g. cn=admin,dc=example,dc=com"
              error={errors.ldapBindDn}
              required
            />
            <div>
              <label className="text-xs font-medium d block mb-1.5">
                Bind Password
                <span className="text-red-500 ml-0.5">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={config.ldapBindPassword || ""}
                  onChange={(e) =>
                    updateField("ldapBindPassword", e.target.value)
                  }
                  placeholder={
                    config.ldapPasswordSet
                      ? "Leave blank to keep saved password"
                      : "Enter bind password"
                  }
                  className={`w-full text-sm bg-[#f5f7fb] dark:bg-white/[0.04] rounded-lg pl-3 pr-9 py-2.5 outline-none border transition-all placeholder:text-[#94a3b8] dark:placeholder:text-white/25 ${
                    errors.ldapBindPassword
                      ? "border-red-400 dark:border-red-500/50 focus:border-red-500 focus:ring-2 focus:ring-red-500/10"
                      : "border-[#e2e8f0] dark:border-white/[0.07] focus:border-indigo-500 dark:focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10"
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 d hover:text-foreground transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="size-4" />
                  ) : (
                    <Eye className="size-4" />
                  )}
                </button>
              </div>
              {errors.ldapBindPassword && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                  <AlertTriangle className="size-3" />
                  {errors.ldapBindPassword}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Directory Search ── */}
      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-border bg-[#f8fafc] dark:bg-white/[0.02]">
          <div className="size-8 rounded-lg bg-teal-50 dark:bg-teal-500/10 flex items-center justify-center">
            <Users className="size-4 text-teal-600 dark:text-teal-400" />
          </div>
          <div>
            <h3 className="text-sm font-semibold">Directory Search</h3>
            <p className="text-[11px] d">
              Configure how users and groups are discovered
            </p>
          </div>
        </div>

        <div className="divide-y divide-border">
          {/* User lookup */}
          <div className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="size-6 rounded-md bg-sky-50 dark:bg-sky-500/10 flex items-center justify-center">
                <Users className="size-3 text-sky-600 dark:text-sky-400" />
              </div>
              <span className="text-xs font-bold d tracking-wider">
                User lookup
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">
              <Field
                label="User search base"
                value={config.ldapUserSearchBase}
                onChange={(v) => updateField("ldapUserSearchBase", v)}
                placeholder="e.g. ou=users"
              />
              <Field
                label={ATTR_LABELS.ldapUserSearchFilter}
                value={config.ldapUserSearchFilter}
                onChange={(v) => updateField("ldapUserSearchFilter", v)}
                placeholder="e.g. sAMAccountName"
                error={errors.ldapUserSearchFilter}
                help="LDAP attribute used to identify a user by login name. AD: sAMAccountName. OpenLDAP: uid. Enter just the attribute name, not a filter."
              />
              <Field
                label={ATTR_LABELS.ldapUserEmailAttr}
                value={config.ldapUserEmailAttr}
                onChange={(v) => updateField("ldapUserEmailAttr", v)}
                placeholder="e.g. mail"
                error={errors.ldapUserEmailAttr}
                help="LDAP attribute on the user entry that holds the email address. Default: mail."
              />
              <Field
                label={ATTR_LABELS.ldapUserDisplayNameAttr}
                value={config.ldapUserDisplayNameAttr}
                onChange={(v) => updateField("ldapUserDisplayNameAttr", v)}
                placeholder="e.g. displayName"
                error={errors.ldapUserDisplayNameAttr}
                help="LDAP attribute on the user entry that holds the display / full name. Default: displayName."
              />
            </div>
          </div>

          {/* Group lookup */}
          <div className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="size-6 rounded-md bg-violet-50 dark:bg-violet-500/10 flex items-center justify-center">
                <Shield className="size-3 text-violet-600 dark:text-violet-400" />
              </div>
              <span className="text-xs font-bold d tracking-wider">
                Group lookup
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">
              <Field
                label="Group search base"
                value={config.ldapGroupSearchBase}
                onChange={(v) => updateField("ldapGroupSearchBase", v)}
                placeholder="e.g. ou=groups"
              />
              <Field
                label={ATTR_LABELS.ldapGroupNameAttr}
                value={config.ldapGroupNameAttr}
                onChange={(v) => updateField("ldapGroupNameAttr", v)}
                placeholder="e.g. cn"
                error={errors.ldapGroupNameAttr}
                help="LDAP attribute on the group entry that names the group. Used to look up groups by name. Default: cn."
              />
              <Field
                label={ATTR_LABELS.ldapGroupMemberAttribute}
                value={config.ldapGroupMemberAttribute}
                onChange={(v) => updateField("ldapGroupMemberAttribute", v)}
                placeholder="e.g. member"
                error={errors.ldapGroupMemberAttribute}
                help="Attribute on a group entry that lists its members' DNs. member (groupOfNames — typical AD/OpenLDAP), uniqueMember (groupOfUniqueNames), memberUid (posixGroup)."
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Action Buttons ── */}
      <div className="flex items-center justify-end gap-2 pt-2">
        <button
          onClick={handleTest}
          disabled={testing}
          className="h-9 flex items-center gap-2 px-4 rounded-xl border border-border bg-background text-sm font-medium hover:bg-accent transition-all disabled:opacity-40"
        >
          {testing ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Activity className="size-4" />
          )}
          {testing ? "Testing" : "Test Connection"}
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="h-9 flex items-center gap-2 px-4 rounded-xl bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-all shadow-[0_4px_14px_rgba(79,70,229,0.3)] dark:shadow-[0_4px_14px_rgba(139,92,246,0.3)] hover:-translate-y-px disabled:opacity-40"
        >
          {saving ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Save className="size-4" />
          )}
          {saving ? "Saving" : "Save Configuration"}
        </button>
      </div>
    </div>
  );
}

/* ── Form field ── */

function Field({
  label,
  value,
  onChange,
  placeholder,
  error,
  type = "text",
  required,
  help,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
  type?: "text" | "password" | "number";
  required?: boolean;
  help?: string;
}) {
  return (
    <div>
      <label className="text-xs font-medium d block mb-1.5">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        title={help}
        className={`w-full text-sm bg-[#f5f7fb] dark:bg-white/[0.04] rounded-lg px-3 py-2.5 outline-none border transition-all placeholder:text-[#94a3b8] dark:placeholder:text-white/25 ${
          error
            ? "border-red-400 dark:border-red-500/50 focus:border-red-500 focus:ring-2 focus:ring-red-500/10"
            : "border-[#e2e8f0] dark:border-white/[0.07] focus:border-indigo-500 dark:focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10"
        }`}
      />
      {error && (
        <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
          <AlertTriangle className="size-3" />
          {error}
        </p>
      )}
    </div>
  );
}
